import React from 'react';
import '../styles/LegalPages.css';

function TermsOfService() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="terms" className="legal-page section">
      <div className="container">
        <div className="legal-content">
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-updated">Last Updated: January 2025</p>

          <div className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using FortIf's AI-powered resume screening platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </p>
            <p>
              FortIf is operated by FortIf Technologies, located at 806, Lokmanya Tilak Road, Near Borivali West Station, Borivali West, Mumbai, Maharashtra 400092, India.
            </p>
          </div>

          <div className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              FortIf provides an AI-powered platform for resume screening and candidate evaluation. Our Service includes:
            </p>
            <ul>
              <li>Automated resume parsing and analysis</li>
              <li>AI-driven candidate matching and scoring</li>
              <li>Bias-aware screening and fairness metrics</li>
              <li>Candidate ranking and shortlisting tools</li>
              <li>Integration capabilities with third-party systems</li>
              <li>Analytics and reporting features</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>3. Account Registration and Security</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To use our Service, you must create an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials.
            </p>

            <h3>3.2 Account Responsibility</h3>
            <p>You agree to:</p>
            <ul>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Ensure all information provided is accurate and up-to-date</li>
              <li>Be responsible for all activities under your account</li>
              <li>Maintain the security of your password and login credentials</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>4. Acceptable Use Policy</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws, including employment and anti-discrimination laws</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Reverse engineer, decompile, or attempt to extract our algorithms</li>
              <li>Use the Service to discriminate against candidates based on protected characteristics</li>
              <li>Scrape, copy, or redistribute our content without permission</li>
              <li>Resell or commercially exploit the Service without authorization</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>5. Data and Privacy</h2>
            <h3>5.1 Your Data</h3>
            <p>
              You retain all rights to the resume data and job descriptions you upload. By using our Service, you grant us a limited license to process this data solely to provide our screening services.
            </p>

            <h3>5.2 Candidate Data Compliance</h3>
            <p>
              You represent and warrant that you have obtained all necessary consents and comply with all applicable laws (including GDPR, EEOC guidelines, and local employment laws) when uploading candidate data.
            </p>

            <h3>5.3 Data Processing</h3>
            <p>
              We process data in accordance with our Privacy Policy. You acknowledge that AI screening involves automated decision-making and agree to use our explainability features to ensure fair hiring practices.
            </p>
          </div>

          <div className="legal-section">
            <h2>6. Subscription and Payment</h2>
            <h3>6.1 Fees</h3>
            <p>
              Access to certain features requires a paid subscription. All fees are stated in the pricing section and are subject to change with 30 days' notice.
            </p>

            <h3>6.2 Billing</h3>
            <p>
              Subscriptions are billed in advance on a monthly or annual basis. You authorize us to charge your payment method automatically. Failure to pay may result in suspension or termination of your account.
            </p>

            <h3>6.3 Refunds</h3>
            <p>
              Refunds are provided at our discretion. Free trial periods may be offered without obligation to continue. Contact <a href="mailto:info@fortif.app">info@fortif.app</a> for refund requests.
            </p>
          </div>

          <div className="legal-section">
            <h2>7. Intellectual Property</h2>
            <h3>7.1 Our Property</h3>
            <p>
              The Service, including all AI algorithms, software, designs, text, graphics, and trademarks, is owned by FortIf and protected by intellectual property laws. You may not copy, modify, or distribute our proprietary content without written permission.
            </p>

            <h3>7.2 Feedback</h3>
            <p>
              Any feedback, suggestions, or ideas you provide about the Service become our property, and we may use them without compensation or attribution.
            </p>
          </div>

          <div className="legal-section">
            <h2>8. AI and Automated Decisions</h2>
            <h3>8.1 AI Accuracy</h3>
            <p>
              While our AI is designed for high accuracy, we do not guarantee perfect results. You acknowledge that AI-driven screening is a tool to assist, not replace, human judgment in hiring decisions.
            </p>

            <h3>8.2 Bias Mitigation</h3>
            <p>
              We employ bias-mitigation techniques, but you remain responsible for ensuring your hiring practices comply with anti-discrimination laws. Use our explainability features and fairness metrics to audit decisions.
            </p>

            <h3>8.3 Final Hiring Decisions</h3>
            <p>
              You acknowledge that final hiring decisions are made by you, not our AI. FortIf is a screening tool and does not make employment decisions on your behalf.
            </p>
          </div>

          <div className="legal-section">
            <h2>9. Service Availability and Support</h2>
            <p>
              We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. Scheduled maintenance will be communicated in advance. Support is available via email at <a href="mailto:support@fortif.app">support@fortif.app</a> during business hours (Monday-Friday, 9 AM - 6 PM IST).
            </p>
          </div>

          <div className="legal-section">
            <h2>10. Termination</h2>
            <h3>10.1 By You</h3>
            <p>
              You may terminate your account at any time by contacting us. Upon termination, you will lose access to the Service, though we may retain certain data as required by law or our retention policies.
            </p>

            <h3>10.2 By Us</h3>
            <p>
              We reserve the right to suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or for any other reason at our discretion.
            </p>
          </div>

          <div className="legal-section">
            <h2>11. Disclaimers and Limitation of Liability</h2>
            <h3>11.1 Service Warranty</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE ERROR-FREE, SECURE, OR MEET YOUR REQUIREMENTS.
            </p>

            <h3>11.2 Liability Limitation</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, FORTIF SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR BUSINESS INTERRUPTION, EVEN IF ADVISED OF THE POSSIBILITY.
            </p>
            <p>
              Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </div>

          <div className="legal-section">
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless FortIf, its officers, employees, and affiliates from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or infringement of third-party rights.
            </p>
          </div>

          <div className="legal-section">
            <h2>13. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of India. Any disputes shall be resolved through binding arbitration in Mumbai, Maharashtra, in accordance with the Arbitration and Conciliation Act, 1996.
            </p>
          </div>

          <div className="legal-section">
            <h2>14. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. Material changes will be notified via email or platform notice. Continued use after changes constitutes acceptance of the new Terms.
            </p>
          </div>

          <div className="legal-section">
            <h2>15. Miscellaneous</h2>
            <h3>15.1 Entire Agreement</h3>
            <p>
              These Terms, along with our Privacy Policy, constitute the entire agreement between you and FortIf.
            </p>

            <h3>15.2 Severability</h3>
            <p>
              If any provision is found unenforceable, the remaining provisions will remain in effect.
            </p>

            <h3>15.3 No Waiver</h3>
            <p>
              Failure to enforce any provision does not waive our right to enforce it later.
            </p>
          </div>

          <div className="legal-section">
            <h2>16. Contact Information</h2>
            <p>For questions about these Terms, please contact:</p>
            <div className="contact-details">
              <p><strong>FortIf Technologies</strong></p>
              <p>806, Lokmanya Tilak Road</p>
              <p>Near Borivali West Station</p>
              <p>Borivali West, Mumbai</p>
              <p>Maharashtra 400092, India</p>
              <p>Email: <a href="mailto:info@fortif.app">info@fortif.app</a></p>
              <p>Support: <a href="mailto:support@fortif.app">support@fortif.app</a></p>
            </div>
          </div>

          <div className="back-to-top">
            <button onClick={scrollToTop} className="btn btn-secondary">
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TermsOfService;
